import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Form, Alert, Row, Col, Spinner } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { FaPen, FaCheck, FaTimes, FaSignature } from 'react-icons/fa';

const Profile = () => {
  const { currentUser, userRole } = useAuth();
  const [userData, setUserData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    nama: '',
    email: '',
    nim: '',
    nip: '',
    prodi: '',
    fakultas: '',
    jurusan: '',
    phone: '',
  });
  const [signature, setSignature] = useState(null);
  const [signatureURL, setSignatureURL] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        const userDoc = await getDoc(doc(db, "users", currentUser.uid));
        
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserData(data);
          setFormData({
            nama: data.nama || '',
            email: data.email || '',
            nim: data.nim || '',
            nip: data.nip || '',
            prodi: data.prodi || '',
            fakultas: data.fakultas || '',
            jurusan: data.jurusan || '',
            phone: data.phone || '',
          });
          
          // Get signature URL if exists
          if (data.signatureURL) {
            setSignatureURL(data.signatureURL);
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        setError("Terjadi kesalahan saat mengambil data profil");
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSignatureChange = (e) => {
    if (e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // Validate file type
      if (!file.type.match('image/(jpeg|jpg|png)')) {
        setError("File harus berupa gambar (jpeg, jpg, atau png)");
        return;
      }
      
      // Validate file size (max 1MB)
      if (file.size > 1024 * 1024) {
        setError("Ukuran file terlalu besar (maksimal 1MB)");
        return;
      }
      
      // Convert to base64 for preview and storage
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        setSignature(reader.result); // Store as base64 string
        setSignatureURL(reader.result); // Show preview immediately
      };
      reader.onerror = () => {
        setError("Gagal membaca file tanda tangan");
      };
      
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setError('');
      setSuccess('');
      setUploading(true);
      
      // Update user data in Firestore
      const userRef = doc(db, "users", currentUser.uid);
      const updateData = {
        ...formData
      };
      
      // Store signature as base64 if a new one is selected
      if (signature) {
        // Using base64 string directly instead of Firebase Storage
        updateData.signatureURL = signature;
      }
      
      // Update the document
      await updateDoc(userRef, updateData);
      
      setSuccess("Profil berhasil diperbarui");
      setIsEditing(false);
      
    } catch (error) {
      console.error("Error updating profile:", error);
      setError("Terjadi kesalahan saat memperbarui profil");
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-4 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Profil Pengguna</h4>
          {!isEditing ? (
            <Button variant="primary" onClick={() => setIsEditing(true)}>
              <FaPen className="me-2" /> Edit Profil
            </Button>
          ) : (
            <div className="d-flex gap-2">
              <Button variant="success" onClick={handleSubmit} disabled={uploading}>
                <FaCheck className="me-2" /> Simpan
              </Button>
              <Button variant="danger" onClick={() => setIsEditing(false)} disabled={uploading}>
                <FaTimes className="me-2" /> Batal
              </Button>
            </div>
          )}
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
          
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nama Lengkap</Form.Label>
                  <Form.Control
                    type="text"
                    name="nama"
                    value={formData.nama}
                    onChange={handleChange}
                    disabled={!isEditing}
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    disabled={true} // Email tidak bisa diubah
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Nomor Telepon</Form.Label>
                  <Form.Control
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    disabled={!isEditing}
                  />
                </Form.Group>
                
                {userRole === 'mahasiswa' && (
                  <Form.Group className="mb-3">
                    <Form.Label>NIM</Form.Label>
                    <Form.Control
                      type="text"
                      name="nim"
                      value={formData.nim}
                      onChange={handleChange}
                      disabled={!isEditing}
                    />
                  </Form.Group>
                )}
                
                {userRole === 'dosen' && (
                  <Form.Group className="mb-3">
                    <Form.Label>NIP</Form.Label>
                    <Form.Control
                      type="text"
                      name="nip"
                      value={formData.nip}
                      onChange={handleChange}
                      disabled={!isEditing}
                    />
                  </Form.Group>
                )}
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Program Studi</Form.Label>
                  <Form.Control
                    type="text"
                    name="prodi"
                    value={formData.prodi}
                    onChange={handleChange}
                    disabled={!isEditing}
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Fakultas</Form.Label>
                  <Form.Control
                    type="text"
                    name="fakultas"
                    value={formData.fakultas}
                    onChange={handleChange}
                    disabled={!isEditing}
                  />
                </Form.Group>
                
                {userRole === 'dosen' && (
                  <Form.Group className="mb-3">
                    <Form.Label>Jurusan</Form.Label>
                    <Form.Control
                      type="text"
                      name="jurusan"
                      value={formData.jurusan}
                      onChange={handleChange}
                      disabled={!isEditing}
                    />
                  </Form.Group>
                )}
                
                <Form.Group className="mb-3">
                  <Form.Label className="d-flex align-items-center">
                    <FaSignature className="me-2" /> Tanda Tangan Digital
                  </Form.Label>
                  
                  {isEditing ? (
                    <div>
                      <Form.Control
                        type="file"
                        accept="image/jpeg, image/jpg, image/png"
                        onChange={handleSignatureChange}
                        disabled={uploading}
                      />
                      <Form.Text className="text-muted">
                        Upload gambar tanda tangan Anda (format JPG, JPEG, atau PNG, maks. 1MB)
                      </Form.Text>
                    </div>
                  ) : null}
                  
                  {signatureURL && (
                    <div className="mt-3 border p-3">
                      <p className="mb-2">Tanda Tangan Saat Ini:</p>
                      <img 
                        src={signatureURL} 
                        alt="Tanda tangan" 
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '100px', 
                          border: '1px solid #ddd', 
                          borderRadius: '4px',
                          padding: '5px',
                          backgroundColor: '#fff'
                        }} 
                      />
                    </div>
                  )}
                  
                  {!signatureURL && !isEditing && (
                    <Alert variant="warning">
                      Anda belum menambahkan tanda tangan digital. Silakan edit profil untuk menambahkan.
                    </Alert>
                  )}
                </Form.Group>
              </Col>
            </Row>
            
            {isEditing && (
              <div className="d-grid gap-2 d-md-flex justify-content-md-end mt-3">
                <Button variant="success" type="submit" disabled={uploading}>
                  {uploading ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                      Menyimpan...
                    </>
                  ) : (
                    <>
                      <FaCheck className="me-2" /> Simpan Perubahan
                    </>
                  )}
                </Button>
                <Button variant="danger" onClick={() => setIsEditing(false)} disabled={uploading}>
                  <FaTimes className="me-2" /> Batal
                </Button>
              </div>
            )}
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Profile;
